import React, { FC, useContext, useState } from 'react'

import { Tooltip } from '@mui/material'
import { useParams } from 'react-router'
import { Loader2Icon } from 'lucide-react'

import TooltipIcon from '../../../../../helpers/TooltipIcon'
import { Button } from '../../../../../components/ui/button'
import { ProfileFields } from './ProfileFields'
import ViewsLoader from '../../../../../components/loader/ViewsLoader'

import {
  selectRegistrationByDay,
  selectTeamTabData,
  setTeamTabData,
} from '../../../../../store/registration/registrationSlice'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectedEvent } from '../../../../../store/events/eventsSlice'

import { HandleModalContext } from '../../../../../layout/mainlayout/MainLayout'
import { getUserFullName } from '../../../../../helpers/helpers'
import { parseAddress } from '../../../../organizer/manage/tabs/reports/eventResultsReportUtils'
import { sortUsersByRegistrar } from '../../../../../helpers/registration-on-event'
import { getRegisterTeamData } from '../../../../../helpers/teamMembers'

import { IUserPublicInterface } from '../../../../../models/users-public/users-public.interface'
import ITypes from '../../../event-registration-tabs/hooks/useEventRegistrationTabs.types'

import { MODAL_CONSTS } from '../../../../../const/modal-const'

interface PeoplesProps {
  teamTabDataWithUser: IUserPublicInterface[]
  setTeamTabDataWithUser: (value: IUserPublicInterface[]) => void
  mainLoading: boolean
}
export const Peoples: FC<PeoplesProps> = ({
  teamTabDataWithUser,
  setTeamTabDataWithUser,
  mainLoading,
}) => {
  const dispatch = useAppDispatch()
  const registrationByDay = useAppSelector(selectRegistrationByDay)

  const { userId } = useParams<ITypes['IParams']>()

  const handleModalContext = useContext(HandleModalContext)
  const handleModal = handleModalContext?.handleModal
  const teamTabData = useAppSelector(selectTeamTabData)
  const { Event } = useAppSelector(selectedEvent)

  const [loading, setLoading] = useState(false)

  const removeHandler = (id: string) => {
    let userIsRegistered = false
    if (registrationByDay) {
      userIsRegistered = !!registrationByDay.find((rider) => rider.riderId === id)
    }

    handleModalContext?.handleModal?.(true, MODAL_CONSTS.REMOVE_CONFIRMATION, {
      removeItem: () => {
        const teamTabDataUpdated = teamTabData.filter((user) => user.memberId !== id)
        dispatch(setTeamTabData(teamTabDataUpdated))
      },
      isBtnOk: userIsRegistered,
      alertText: userIsRegistered ? (
        <>
          <h5 className="text-xl text-center mb-10 leading-relaxed font-semibold">
            The rider is already registered. <br /> You can remove them in the next step.
          </h5>
        </>
      ) : (
        <div>
          <h5 className="font-semibold text-xl mb-10">
            Are you sure you want to remove this rider?
          </h5>
        </div>
      ),
    })
  }

  if (mainLoading) {
    return (
      <div className="w-full lg:w-[48%] flex border flex-col rounded-md border-[#D3DAEE] p-4 flex-1 gap-5 pb-[300px]">
        <ViewsLoader
          className="flex items-center md:w-80 w-full justify-center my-3"
          size="md"
          color="red"
        />
      </div>
    )
  }

  return (
    <div className="w-full lg:w-[48%] flex border flex-col rounded-md border-[#D3DAEE] p-4 gap-2.5 min-h-[300px]">
      <div className="flex w-full justify-between">
        <div>
          <div className="flex items-center">
            <h3 className="text-[#122B46] text-[22px] font-bold mr-1">People</h3>
            <Tooltip title={''} placement="top" arrow>
              <button
                type="button"
                className=""
                onClick={() => handleModal?.(true, MODAL_CONSTS.REGISTRATION_PEOPLE)}
              >
                <TooltipIcon color="#122B46" />
              </button>
            </Tooltip>
          </div>
          <p className="text-[#122B4680] text-[14px]">Fix errors to register</p>
        </div>
      </div>
      {teamTabDataWithUser.length <= 0 ? (
        <div className="rounded-lg text-black/50 bg-black/[2%] w-full min-h-[112px] text-xs p-4 flex flex-col items-center justify-center">
          <p>No people added to this entry</p>
          <Button
            variant="link"
            disabled={loading}
            onClick={async () => {
              setLoading(true)
              const { userTeamMembers, competitor } = await getRegisterTeamData({
                teamTabData,
                userId,
              })
              setLoading(false)
              handleModal?.(true, MODAL_CONSTS.REGISTER_TEAM, {
                userTeamMembers: userTeamMembers,
                competitor,
              })
            }}
            className="text-black/50 underline hover:no-underline flex items-center gap-1"
          >
            {loading && <Loader2Icon className="animate-spin w-4" />}
            Add person to start
          </Button>
        </div>
      ) : (
        <>
          {sortUsersByRegistrar(teamTabDataWithUser, userId).map((item, index) => {
            return (
              <ProfileFields
                eventRequiredFields={Event?.requiredFields || []}
                key={index}
                user={item}
                editHandler={(data) => {
                  handleModal?.(true, MODAL_CONSTS.UPDATE_USER_NUMBER, {
                    numberLogo: data.logo,
                    fieldName: data.title,
                    fieldKey: data.fieldKey,
                    fieldType: data.type,
                    userData: item,
                    onSave: ({ updatedUserData }: { updatedUserData: { [_: string]: string } }) => {
                      const teamTabDataWithUserUpdated = teamTabDataWithUser.map((user) => {
                        if (user.id === item.id) {
                          return { ...user, ...updatedUserData }
                        } else {
                          return user
                        }
                      })
                      setTeamTabDataWithUser(teamTabDataWithUserUpdated)
                    },
                  })
                }}
                headerImgSrc={item?.userProfilePicture || ''}
                headerTitle={getUserFullName(item)}
                headerSubTitle={`${parseAddress(item.userAddress || '')?.city || 'No city'}, ${parseAddress(item.userAddress || '')?.state || 'No State'}`}
                teamTabDataWithUser={teamTabDataWithUser}
                setTeamTabDataWithUser={setTeamTabDataWithUser}
                removeHandler={() => removeHandler(item.id)}
                isDeleteBtnHide={item.id === userId}
              />
            )
          })}
          <Button
            variant="underline"
            disabled={mainLoading}
            onClick={async () => {
              setLoading(true)
              const { userTeamMembers, competitor } = await getRegisterTeamData({
                teamTabData,
                userId,
              })
              setLoading(false)
              handleModal?.(true, MODAL_CONSTS.REGISTER_TEAM, {
                userTeamMembers: userTeamMembers,
                competitor,
              })
            }}
            className="p-4 tex-sm font-normal rounded-lg flex items-center gap-1"
          >
            {loading && <Loader2Icon className="animate-spin w-4" />}
            Add person
          </Button>
        </>
      )}
    </div>
  )
}
